import { ApiCall } from '@api';
import { getCurrency } from 'planoplan-ui-kit/core/helpers/get-currency';
import {
  normalizeBlog,
  normalizeBlogPage,
  normalizeBlogPopular,
  normalizeBlogAlternateLinks,
} from '@modules/normalize/blog';
import { normalizeComments } from '@modules/normalize/comments';
import { normalizeTextPage, normalizeAndClearText } from '@modules/normalize/text-page';
import { normalizePricing } from '@modules/normalize/pricing';
import { normalizeHome } from '@modules/normalize/home';
import { normalizeBanner } from '@modules/normalize/banner';
import { addIdForTitles } from '@modules/normalize/library';
import {
  normalizeEducationButtons,
  normalizeEducationVideo,
  normalizeEducationInstructions,
  normalizeEducationCourses,
} from '@modules/normalize/education';
import { normalizePreview } from '@modules/normalize/preview';
import { normalizeGalleryProject, normalizeGalleryProjectTag, normalizeGalleryQuery } from '@modules/normalize/gallery';
import { HELP_CENTER, PROCESSING_ITEMS, HELP_CENTER_ARTICLES_MAX_LIMIT } from '@constants';
import {
  helpCenterSectionsAdapter,
  helpCenterSectionAdapter,
  helpCenterContentAdapter,
  helpCenterCourseAdapter,
  helpCenterContentInCourseViewAdapter,
  helpCenterSearchAdapter,
} from './adapters';
import * as actions from '@store/actions';

export const getCommonData = () => async (dispatch, getState) => {
  const {
    common = {},
    settings: { locale },
  } = getState();
  const { contentLang = '' } = common;

  // Запрашиваем если нет данных и изменился язык
  if (Object.keys(common).length > 1 && contentLang === locale) {
    return;
  }

  try {
    const response = await ApiCall.getContent({ groups: 'common', locale });
    const { data = {} } = response;
    const { success, errorText } = data;

    if (!success) {
      throw new Error(errorText);
    }

    const banner = normalizeBanner(data.data);
    const contentLang = locale;

    dispatch(actions.setCommonData({ banner, contentLang }));
  } catch (e) {
    dispatch(actions.setCommonData({ banner: {} }));
  }
};

export const getHome = () => async (dispatch, getState) => {
  const {
    settings: { locale },
  } = getState();

  try {
    const response = await ApiCall.getContent({ groups: 'home', locale });
    const { data = {} } = response;
    const { success, errorText } = data;

    if (!success) {
      throw new Error(errorText);
    }

    const { directSpeech, main_faq } = normalizeHome(data.data);

    return { directSpeech, main_faq, locale };
  } catch (error) {
    return { directSpeech: [] };
  }
};

export const getGoods = () => async (dispatch, getState) => {
  const {
    settings: { locale, getCtx },
  } = getState();

  try {
    const response = await ApiCall.getGoods({ locale, ctx: getCtx() });
    const { data = {} } = response;
    const { success, errorText, data: tariffData = {} } = data;

    if (!success) {
      throw new Error(errorText);
    }

    return tariffData;
  } catch (error) {
    dispatch(actions.setError(PROCESSING_ITEMS.GET_GOODS, error));

    return null;
  }
};

export const getProfile = () => async (dispatch, getState) => {
  const {
    settings: { locale, getCtx },
  } = getState();

  try {
    const response = await ApiCall.getProfile({ locale, ctx: getCtx() });
    const { data = {} } = response;

    return data;
  } catch (error) {
    dispatch(actions.setError(PROCESSING_ITEMS.GET_PROFILE, error));

    return null;
  }
};

export const getPricing = () => async (dispatch, getState) => {
  const {
    user,
    settings: { locale, clientIp },
  } = getState();

  try {
    const response = await ApiCall.getPricing({ locale, clientIp });
    const contentResponse = await ApiCall.getContent({
      groups: 'tariffs',
      locale,
    });

    const { success, errorText, data: pricingData } = response?.data;
    const contentData = contentResponse?.data?.data?.tariffs?.blocks;

    if (!success) {
      throw new Error(errorText);
    }

    const { pricing, currency: currencyValue } = pricingData;
    const currency = getCurrency(currencyValue);
    const { renders, tariffs, tariffsContent, tokens, goodsWithPriceToken } = normalizePricing({
      pricing,
      user,
      currency,
      contentData,
    });
    return { renders, tariffs, currency, tariffsContent, tokens, goodsWithPriceToken };
  } catch (error) {
    dispatch(actions.setError(PROCESSING_ITEMS.GET_PRICING, error));

    return { renders: [], tariffs: [], currency: '', tariffsContent: {} };
  }
};

export const getPricingAndGoods = (alias) => async (dispatch, getState) => {
  const {
    user,
    settings: { locale, clientIp },
  } = getState();

  try {
    const response = await ApiCall.getPricing({ locale, clientIp });
    const contentResponse = await ApiCall.getContent({
      groups: 'pricing',
      blocks: 'short_banners',
      locale,
      getParams: { exclude_fields: 'short_description' },
    });

    const { data: dataPricing = {} } = response;
    const { data: dataContent = {} } = contentResponse;
    const { success, errorText, data: pricingData } = dataPricing;
    const { data: contentData } = dataContent;

    if (!success) {
      throw new Error(errorText);
    }

    const { pricing, currency: currencyValue } = pricingData;
    const currency = getCurrency(currencyValue);
    const { tariffs } = normalizePricing({
      pricing,
      user,
      currency,
      contentData,
    });

    const sets = pricing.find((item) => item.alias === alias).sets;

    const goods = pricing
      .find((item) => item.alias === alias)
      .goods.reduce((acc, item) => {
        const { alias } = item;
        if (!acc[alias]) {
          acc[alias] = [];
        }
        acc[alias].push(item);
        return acc;
      }, {});

    return { tariffs, currency, goods, sets, currencyValue };
  } catch (error) {
    dispatch(actions.setError(PROCESSING_ITEMS.GET_PRICING, error));

    return { renders: [], tariffs: [], goods: {}, sets: {} };
  }
};

export const getBlog = (query) => async (dispatch, getState) => {
  const {
    settings: { locale },
  } = getState();

  try {
    const { category: queryCategory, page } = query;
    const category = Array.isArray(queryCategory) ? queryCategory[0] : queryCategory;
    const response = await ApiCall.getBlog({ category, page, locale });
    const { data = {} } = response;
    const { success, errorText } = data;

    if (!success) {
      throw new Error(errorText);
    }

    const { articles, navigations, pageCount } = normalizeBlog(data.data);
    const currentCategory = category ? category : 'all';

    return { articles, navigations, pageCount, currentCategory };
  } catch (error) {
    dispatch(actions.setError(PROCESSING_ITEMS.GET_BLOG, error));

    return { articles: [], navigations: [], pageCount: 0 };
  }
};

export const getBlogPage = (query) => async (dispatch, getState) => {
  const {
    settings: { locale },
  } = getState();

  try {
    const itemResponse = await ApiCall.getBlogPage({ ...query, locale });
    const popularResponse = await ApiCall.getPopularBlog({ locale });

    const { data: itemData = {} } = itemResponse;
    const { data: popularData = {} } = popularResponse;

    const { success, errorText } = itemData;

    if (!success) {
      throw new Error(errorText);
    }

    const content = normalizeBlogPage(itemData.data, { locale });
    const popular = normalizeBlogPopular(popularData.data);
    const comments = normalizeComments(itemData.data, { locale });
    const alternateLinks = normalizeBlogAlternateLinks(itemData.data);

    return { content, popular, comments, alternateLinks };
  } catch (error) {
    dispatch(actions.setError(PROCESSING_ITEMS.GET_BLOG_PAGE, error));

    return { content: {}, popular: [], comments: [] };
  }
};

export const getAgreements = () => async (dispatch, getState) => {
  const {
    settings: { locale },
  } = getState();

  try {
    const response = await ApiCall.getContent({ groups: 'agreements', locale });
    const { data = {} } = response;
    const { success, errorText } = data;

    if (!success) {
      throw new Error(errorText);
    }

    const { sections, contents, important } = normalizeTextPage(data.data, {
      groupKey: 'agreements',
      contentKey: 'agreements_content',
      importantKey: 'agreements_important',
    });

    return { sections, contents, important };
  } catch (error) {
    dispatch(actions.setError(PROCESSING_ITEMS.GET_AGREEMENTS, error));

    return { sections: [], contents: [], important: '' };
  }
};

export const getPrivacyPolicy = () => async (dispatch, getState) => {
  const {
    settings: { locale },
  } = getState();

  try {
    const response = await ApiCall.getContent({ groups: 'privacy_policy', locale });
    const { data = {} } = response;
    const { success, errorText } = data;

    if (!success) {
      throw new Error(errorText);
    }

    const { sections, contents, important } = normalizeTextPage(data.data, {
      groupKey: 'privacy_policy',
      contentKey: 'policy_content',
      importantKey: 'policy_important',
    });

    return { sections, contents, important };
  } catch (error) {
    dispatch(actions.setError(PROCESSING_ITEMS.GET_PRIVACY_POLICY, error));

    return { sections: [], contents: [], important: '' };
  }
};

export const getLegalInfo = () => async (dispatch, getState) => {
  const {
    settings: { locale },
  } = getState();

  try {
    const response = await ApiCall.getContent({ groups: 'legal_info', locale });
    const { data = {} } = response;
    const { success, errorText } = data;

    if (!success) {
      throw new Error(errorText);
    }

    const { sections, contents, important } = normalizeTextPage(data.data, {
      groupKey: 'legal_info',
      contentKey: 'legal_content',
      importantKey: '',
    });

    return { sections, contents, important };
  } catch (error) {
    dispatch(actions.setError(PROCESSING_ITEMS.GET_LEGAL_INFO, error));

    return { sections: [], contents: [], important: '' };
  }
};

export const getPersonalDataProcessing = () => async (dispatch, getState) => {
  const {
    settings: { locale },
  } = getState();

  try {
    const response = await ApiCall.getContent({ groups: 'personal_data_processing', locale });
    const { data = {} } = response;
    const { success, errorText } = data;

    if (!success) {
      throw new Error(errorText);
    }

    const { sections, contents, important } = normalizeTextPage(data.data, {
      groupKey: 'personal_data_processing',
      contentKey: 'processing_content',
      importantKey: '',
    });

    return { sections, contents, important };
  } catch (error) {
    dispatch(actions.setError(PROCESSING_ITEMS.GET_PERSONAL_DATA_PROCESSING, error));

    return { sections: [], contents: [], important: '' };
  }
};

export const getDownloadPageFAQ = () => async (dispatch, getState) => {
  const {
    settings: { locale },
  } = getState();

  try {
    const response = await ApiCall.getContent({ groups: 'download_faq', locale });
    const { data = {} } = response;
    const { success, errorText } = data;

    if (!success) {
      throw new Error(errorText);
    }
    const faqData = data.data.download_faq.blocks;

    return { faqData };
  } catch (error) {
    dispatch(actions.setError(PROCESSING_ITEMS.GET_FAQ, error));

    return {};
  }
};

export const getUpdates = () => async (dispatch, getState) => {
  const {
    settings: { locale },
  } = getState();

  try {
    const response = await ApiCall.getBlog({ category: 'updates', locale });
    const { data = {} } = response;
    const { success, errorText } = data;

    if (!success) {
      throw new Error(errorText);
    }

    const updates = data.data.items
      .map((item) => ({ caption: item.caption, link: item.link.replace('getblog', 'blog') }))
      .slice(0, 4);

    return { updates };
  } catch (error) {
    dispatch(actions.setError(PROCESSING_ITEMS.GET_BLOG, error));

    return {};
  }
};

export const getEducationVideos =
  (subCategory = '') =>
  async (dispatch, getState) => {
    const {
      settings: { locale },
    } = getState();

    try {
      const category = subCategory && subCategory !== 'undefined' ? subCategory : '';
      const response = await ApiCall.getContent({ groups: 'education_videos', blocks: category, locale });

      const { data = {} } = response;
      const { success, errorText } = data;

      if (!success) {
        throw new Error(errorText);
      }

      return normalizeEducationVideo(data.data, category);
    } catch (error) {
      return [];
    }
  };

export const getEducationButtons = () => async (dispatch, getState) => {
  const {
    settings: { locale },
  } = getState();

  try {
    const response = await ApiCall.getContent({ groups: 'education_buttons', locale });

    const { data = {} } = response;
    const { success, errorText } = data;

    if (!success) {
      throw new Error(errorText);
    }

    return normalizeEducationButtons(data.data);
  } catch (error) {
    return [];
  }
};

export const getEducationInstructions = () => async (dispatch, getState) => {
  const {
    settings: { locale },
  } = getState();

  try {
    const response = await ApiCall.getContent({ groups: 'education_instructions', locale });

    const { data = {} } = response;
    const { success, errorText } = data;

    if (!success) {
      throw new Error(errorText);
    }

    return normalizeEducationInstructions(data.data);
  } catch (error) {
    return [];
  }
};

export const getEducationCourses = () => async (dispatch, getState) => {
  const {
    settings: { locale },
  } = getState();

  try {
    const response = await ApiCall.getContent({ groups: 'education_courses', locale });

    const { data = {} } = response;
    const { success, errorText } = data;

    if (!success) {
      throw new Error(errorText);
    }

    return normalizeEducationCourses(data.data, locale);
  } catch (error) {
    return [];
  }
};

export const getPreviewScreenshots = (id, params) => async (dispatch, getState) => {
  const { isTour, isEstimate, hiddenColumns, hiddenTypes } = params;
  const {
    settings: { locale },
  } = getState();

  try {
    const response = await ApiCall.getPreviewScreenshots({ id, locale, isTour, isEstimate });

    const { data = {} } = response;
    const { success, errorText } = data;

    if (!success) {
      throw new Error(errorText);
    }

    return normalizePreview(id, data.data, isTour, isEstimate, hiddenColumns, hiddenTypes);
  } catch (error) {
    return null;
  }
};

export const getPreviewDesignProject = (id) => async () => {
  try {
    // eslint-disable-next-line no-console
    console.log('PREVIEW DESIGN REQUEST START', { id });
    const response = await ApiCall.getPreviewDesignProject({ id });
    const responseData = response?.data || {};
    const html = responseData.data?.html || responseData.html;

    // eslint-disable-next-line no-console
    console.log('PREVIEW DESIGN REQUEST RESPONSE', {
      status: response?.status,
      success: responseData.success,
      htmlLength: typeof html === 'string' ? html.length : 0,
    });

    return responseData.success && typeof html === 'string' && html.trim() ? html : null;
  } catch (error) {
    // Design-project hash is not an error for an ordinary Gallery preview.
    // eslint-disable-next-line no-console
    console.info('PREVIEW DESIGN REQUEST ERROR', error);

    return null;
  }
};

export const getGalleryProjects = (query) => async (dispatch, getState) => {
  const {
    settings: { locale },
  } = getState();

  try {
    const tagsResponse = await ApiCall.getGalleryProjectsTags({ locale });

    if (!tagsResponse.data.success) {
      throw new Error(tagsResponse.data.errorText);
    }

    const projectsResponse = await ApiCall.getGalleryProjects({ locale });

    if (!projectsResponse.data.success) {
      throw new Error(projectsResponse.data.errorText);
    }

    const tags = normalizeGalleryProjectTag(tagsResponse.data);
    const projects = normalizeGalleryProject(projectsResponse.data);
    const queryTags = normalizeGalleryQuery(query, tags);

    if (!tags || !projects) {
      throw new Error('Incorrect structure data');
    }

    return {
      tags,
      projects,
      queryTags,
    };
  } catch (error) {
    dispatch(actions.setError(PROCESSING_ITEMS.GET_GALLERY_PROJECTS, error));

    return {
      tags: [],
      projects: [],
      queryTags: [],
    };
  }
};

export const getGalleryProject = (query) => async (dispatch, getState) => {
  const {
    settings: { locale },
  } = getState();

  try {
    const tagsResponse = await ApiCall.getGalleryProjectsTags({ locale });

    if (!tagsResponse.data.success) {
      throw new Error(tagsResponse.data.errorText);
    }

    const projectsResponse = await ApiCall.getGalleryProjects({ locale });

    if (!projectsResponse.data.success) {
      throw new Error(projectsResponse.data.errorText);
    }

    const tags = normalizeGalleryProjectTag(tagsResponse.data);
    const projects = normalizeGalleryProject(projectsResponse.data);

    if (!tags || !projects) {
      throw new Error('Incorrect structure data');
    }

    const project = query.project
      ? projects.find((item) => {
          return item.address.toLowerCase() === query.project;
        })
      : null;

    return {
      tags,
      project,
      projects,
    };
  } catch (error) {
    dispatch(actions.setError(PROCESSING_ITEMS.GET_GALLERY_PROJECTS, error));

    return {
      tags: [],
      project: [],
      projects: [],
    };
  }
};

export const getLibrary = (props) => async (dispatch, getState) => {
  const {
    settings: { locale },
  } = getState();

  try {
    const response = await ApiCall.getLibrary({ locale });
    const { success, errorText, data } = response.data;

    if (!success) {
      throw new Error(errorText);
    }

    return { library: data, ...props };
  } catch (error) {
    console.error(error.message); // eslint-disable-line
    dispatch(actions.setError(PROCESSING_ITEMS.GET_LIBRARY), error);

    return {};
  }
};

export const getLibraryCategory =
  ({ category }) =>
  async (dispatch, getState) => {
    const {
      settings: { locale },
    } = getState();

    try {
      const response = await ApiCall.getLibraryCategory({ locale, group: category });
      const { success, errorText, data } = response.data;

      if (!success) {
        throw new Error(errorText);
      }

      return data;
    } catch (error) {
    console.error(error.message); // eslint-disable-line
      dispatch(actions.setError(PROCESSING_ITEMS.GET_LIBRARY), error);

      return {};
    }
  };

export const getLibraryArticle =
  ({ subcategory, article }) =>
  async (dispatch, getState) => {
    const {
      settings: { locale },
    } = getState();

    const getCategories = async () => {
      const response = await ApiCall.getLibrarySubCategory({ locale, block: subcategory });
      const { success, errorText, data } = response.data;

      if (!success) {
        throw new Error(errorText);
      }

      return Object.values(data)[0];
    };
    const getArticle = async () => {
      const response = await ApiCall.getLibraryArticle({ locale, block: subcategory, article });
      const { success, errorText, data } = response.data;

      if (!success) {
        throw new Error(errorText);
      }

      const dataSubCategory = Object.values(data)[0];
      const dataArticle = Object.values(dataSubCategory.items).find((item) => item.alias === article);

      if (!dataArticle) {
        throw new Error(`Article "${article}" not found`);
      }

      return {
        ...dataArticle,
        form_data: {
          ...dataArticle.form_data,
          text: addIdForTitles(normalizeAndClearText(dataArticle.form_data.text)),
        },
      };
    };

    try {
      const category = await getCategories();
      const article = await getArticle();

      return { category, article };
    } catch (error) {
    console.error(error.message); // eslint-disable-line
      dispatch(actions.setError(PROCESSING_ITEMS.GET_LIBRARY), error);

      return {};
    }
  };

export const getManufacturersData = () => async (dispatch, getState) => {
  const {
    settings: { locale },
  } = getState();

  try {
    const response = await ApiCall.getContent({ groups: 'page_manufacturers', locale });
    const { data = {} } = response;
    const { success, errorText } = data;

    if (!success) {
      throw new Error(errorText);
    }
    const content = data.data.page_manufacturers.blocks;
    return { content };
  } catch (error) {
    dispatch(actions.setError(PROCESSING_ITEMS.GET_FAQ, error));

    return {};
  }
};

export const getDistributorsData = () => async (dispatch, getState) => {
  const {
    settings: { locale },
  } = getState();

  try {
    const response = await ApiCall.getContent({ groups: 'distributors', locale });
    const { data = {} } = response;
    const { success, errorText } = data;

    if (!success) {
      throw new Error(errorText);
    }

    const cards = data.data.distributors.blocks;

    return cards;
  } catch (error) {
    dispatch(actions.setError(PROCESSING_ITEMS.GET_FAQ, error));

    return {};
  }
};

export const getPromotions = () => async (dispatch, getState) => {
  const {
    settings: { locale },
  } = getState();

  try {
    const response = await ApiCall.getContent({ groups: 'promotion', locale });
    const { data = {} } = response;
    const { success, errorText } = data;

    if (!success) {
      throw new Error(errorText);
    }
    const cards = data.data.promotion.blocks.promo_cards;
    return cards;
  } catch (error) {
    dispatch(actions.setError(PROCESSING_ITEMS.GET_FAQ, error));

    return {};
  }
};

export const getHelpSections = () => async (dispatch, getState) => {
  const {
    settings: { locale },
  } = getState();

  try {
    const isConnection = await ApiCall.checkConnection();
    if (isConnection === false) {
      return { isDisconnect: true };
    }
    const response = await ApiCall.getHelpSections({
      groups: HELP_CENTER.MAIN,
      locale,
    });
    const { data = {} } = response;
    const { success, errorText } = data;

    if (!success) {
      throw new Error(errorText);
    }

    return helpCenterSectionsAdapter(data.data);
  } catch (error) {
    dispatch(actions.setError(PROCESSING_ITEMS.GET_HELP, error));
    return {};
  }
};

export const getHelpSection =
  ({ section, category = '', page = 1, limit = HELP_CENTER_ARTICLES_MAX_LIMIT }) =>
  async (dispatch, getState) => {
    const {
      settings: { locale },
    } = getState();

    try {
      const isConnection = await ApiCall.checkConnection();
      if (isConnection === false) {
        return { currentPage: page, isDisconnect: true };
      }

      const response = await ApiCall.getHelpSection({
        section,
        locale,
        category,
        limit,
        page,
      });

      const { success, errorText, data } = response.data;

      if (!success) {
        throw new Error(errorText);
      }

      return helpCenterSectionAdapter({ ...data, currentPage: page });
    } catch (error) {
      console.error(error.message); // eslint-disable-line
      dispatch(actions.setError(PROCESSING_ITEMS.GET_HELP, error));

      return {};
    }
  };

export const getCourse =
  ({ section, module }) =>
  async (dispatch, getState) => {
    const {
      settings: { locale },
    } = getState();

    try {
      const isConnection = await ApiCall.checkConnection();
      if (isConnection === false) {
        return { isDisconnect: true };
      }
      const response = await ApiCall.getHelpSection({
        section,
        locale,
        category: module,
      });

      const { success, errorText, data } = response.data;

      if (!success) {
        throw new Error(errorText);
      }

      return helpCenterCourseAdapter({ ...data });
    } catch (error) {
      console.error(error.message); // eslint-disable-line
      dispatch(actions.setError(PROCESSING_ITEMS.GET_HELP, error));

      return {};
    }
  };

export const getHelpContent =
  ({ section, category, article, isCourse = false }) =>
  async (dispatch, getState) => {
    const {
      settings: { locale },
    } = getState();
    try {
      const isConnection = await ApiCall.checkConnection();
      if (isConnection === false) {
        return { isDisconnect: true };
      }

      const response = await ApiCall.getHelpItem({ locale, section, category, article });

      const { success, errorText, data } = response.data;

      if (!success) {
        throw new Error(errorText);
      }

      if (isCourse) {
        return helpCenterContentInCourseViewAdapter(data);
      }

      return helpCenterContentAdapter(data);
    } catch (error) {
      console.error(error.message); // eslint-disable-line

      dispatch(actions.setError(PROCESSING_ITEMS.GET_HELP_CONTENT, error));

      return {};
    }
  };

export const getHelpSearch =
  ({ query, selectedSection }) =>
  async (dispatch, getState) => {
    const {
      settings: { locale },
    } = getState();

    try {
      const response = await ApiCall.getHelpSearch({ locale, query });
      const { success, errorText, data } = response.data;

      if (!success) throw new Error(errorText);

      const safe = Array.isArray(data) ? data : [];
      const adapted = selectedSection ? helpCenterSearchAdapter(safe, selectedSection) : helpCenterSearchAdapter(safe);

      return adapted;
    } catch (error) {
      console.error(error?.message);
      dispatch(actions.setError(PROCESSING_ITEMS.GET_HELP_CONTENT, error));
      return [];
    }
  };

export const getWebinars = () => async (dispatch, getState) => {
  const {
    settings: { locale },
  } = getState();

  try {
    const response = await ApiCall.getContent({ groups: 'webinars', locale });
    const { data = {} } = response;
    const { success, errorText } = data;

    if (!success) {
      throw new Error(errorText);
    }
    const { webinar_cards, webinar_faq } = data.data.webinars.blocks;
    return { webinar_cards, webinar_faq };
  } catch (error) {
    dispatch(actions.setError(PROCESSING_ITEMS.GET_FAQ, error));

    return {};
  }
};
